import Immutable from 'immutable';

import type { AssetContainerModel, SortEnum } from 'js/src/client';
import { AssetContainerTypes, SlideBackgroundModelTypeEnum } from 'js/src/client';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { getCurrentEditorTab } from 'js/src/data/app';
import type {
    FILTER_BY_LEVELS,
    MediaFolderWithContents,
    ModifiedAssetGenerationResponse,
    SearchContextValueType,
} from 'js/src/data/media';
import {
    AI_MEDIA_SUB_TAB_OPTIONS,
    convertImmutableSlideBackgroundToImmutableAssetContainer,
    findGenerationInList,
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isDAMSearchContext,
    MEDIA_LICENSE_OPTIONS,
    MEDIA_SOURCE_OPTIONS,
    MEDIA_TYPE_OPTIONS,
    RECORDING_FOLDER_NAME,
    SCRIPT_COMPOSER_MEDIA_FOLDER_NAME,
    SEARCH_CONTEXT,
} from 'js/src/data/media';
import StoreORM from 'js/src/data/store-orm';
import {
    immutableAssetContainerModelIsAudio,
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsIcon,
} from 'js/src/data/uploads';
import { ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { createSelector } from 'reselect';

const emptyImmutableList = Immutable.List();

function _formatFilters(filterMap: any) {
    /**
     * returns a list of filters such as ['videos', 'photos', 'marketplace']
     * If an entry is missing that means that matching assets should NOT be returned
     * This is the format that MediaSearchAPIViewSet & TopUploadsTagsAPIView expects
     */
    let filterList = Object.values(MEDIA_SOURCE_OPTIONS).filter((source) => {
        return filterMap.has(source) === false || filterMap.get(source) === true;
    });
    if (filterList.length === 0) {
        // can't have no sources, revert to all
        filterList = Object.values(MEDIA_SOURCE_OPTIONS);
    }
    return filterList;
}
function userInUploadsFolder(state: RootState, searchContext: SearchContextValueType) {
    const isInUploadVisuals = searchContext === SEARCH_CONTEXT.UPLOADED_VISUALS;
    const isInUploadIcons = searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS;
    const isInUploadsFolder = selectActiveMediaFolder(state, searchContext) !== null; // selector returns folder_id

    return (isInUploadVisuals || isInUploadIcons) && isInUploadsFolder;
}

function selectAllMediaResults(state: RootState, searchContext: SearchContextValueType) {
    const retVal = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('media_results')
        : state.media[searchContext]?.media_results;

    if (!retVal) {
        throw new Error('media_results is undefined');
    }

    return retVal;
}

/**
 * Using `createSelector` here to memoize the ORM instance
 * so that components using this selector don't re-render unnecessarily
 */
export const getMediaResultsORM = createSelector(
    (state: RootState, searchContext: SearchContextValueType) =>
        selectAllMediaResults(state, searchContext),
    (mediaResults) => {
        const orm = new StoreORM<AssetContainerModel, string>(mediaResults);
        return orm;
    },
);

export function getCarouselCategoryResults(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext]?.carousel_media_results || {};
}

export function getCategoryMediaResult(
    state: RootState,
    searchContext: SearchContextValueType,
    category: string,
    assetId: string,
): ImmutableMap<AssetContainerModel> | null {
    const carouselResults = getCarouselCategoryResults(state, searchContext);
    const categoryData = carouselResults[category];
    if (!categoryData) {
        return null;
    }
    const orm = new StoreORM<AssetContainerModel, string>(categoryData);
    return orm.getById(assetId);
}

export function getActiveSearchTerm(state: RootState, searchContext: SearchContextValueType) {
    const searchTerm = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('search_term')
        : state.media[searchContext].search_term;

    if (searchTerm === undefined) {
        throw new Error('searchTerm is undefined');
    }
    return searchTerm;
}

export function getActiveCorrectedMediaSearchTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].corrected_search_term;
}

export function getActiveCorrectedMediaSearchTermIndices(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].corrected_search_term_indices;
}

export function getActiveSuggestedMediaSearchTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].suggested_search_term;
}

export function getActiveHighlightedSuggestedMediaSearchTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].highlighted_suggested_search_term;
}

export function getActiveRelatedMediaSearchTerms(
    state: RootState,
    searchContext: SearchContextValueType,
): Immutable.List<string> {
    return state.media[searchContext].related_search_terms;
}

export function getActiveFrequentlyUsedTags(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    const folderId = selectActiveMediaFolder(state, searchContext);
    return getFrequentlyUsedTags(state, searchContext, folderId);
}

export function getFrequentlyUsedTags(
    state: RootState,
    searchContext: SearchContextValueType,
    folderId: number | null,
) {
    return folderId
        ? getFolderDetailsById(state, folderId)?.get('frequently_used_tags')
        : state.media[searchContext].frequently_used_tags;
}

export function getActiveMediaPage(state: RootState, searchContext: SearchContextValueType) {
    const page = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('page')
        : state.media[searchContext].page;

    if (page === undefined) {
        throw new Error('page is undefined');
    }
    return page;
}

export function getActiveMediaPos(
    state: RootState,
    searchContext: SearchContextValueType,
): null | string {
    if (
        searchContext === SEARCH_CONTEXT.TENOR_VISUALS ||
        searchContext === SEARCH_CONTEXT.TENOR_STICKERS
    ) {
        return state.media[searchContext].pos;
    }
    return null;
}

export function getActiveMediaNext(
    state: RootState,
    searchContext: SearchContextValueType,
): null | string {
    if (isDAMSearchContext(searchContext)) {
        return state.media[searchContext].next;
    }
    return null;
}

export function getActiveMediaFilters(
    state: RootState,
    searchContext: SearchContextValueType,
): (MEDIA_LICENSE_OPTIONS | MEDIA_SOURCE_OPTIONS | MEDIA_TYPE_OPTIONS)[] {
    if (searchContext === SEARCH_CONTEXT.FAVORITE_AI_MEDIA) {
        return [MEDIA_TYPE_OPTIONS.ALL];
    }
    if (searchContext === SEARCH_CONTEXT.AI_GENERATED_MEDIA) {
        switch (getActiveAIMediaSubTab(state)) {
            case AI_MEDIA_SUB_TAB_OPTIONS.VIDEO:
                return [MEDIA_TYPE_OPTIONS.VIDEOS];
            case AI_MEDIA_SUB_TAB_OPTIONS.IMAGE:
                return [MEDIA_TYPE_OPTIONS.PHOTOS];
        }
    }
    const sourceFiltersMap = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('sourceFilter')
        : state.media[searchContext].sourceFilter;
    const sourceFilterList = _formatFilters(sourceFiltersMap);
    const selectedType = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('selectedType')
        : state.media[searchContext].selectedType;
    const typeFilters: MEDIA_TYPE_OPTIONS[] =
        selectedType === MEDIA_TYPE_OPTIONS.ALL
            ? [MEDIA_TYPE_OPTIONS.PHOTOS, MEDIA_TYPE_OPTIONS.VIDEOS]
            : [selectedType];
    const selectedLicense = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('selectedLicense')
        : state.media[searchContext].selectedLicense;
    const licenseFilters =
        selectedLicense === MEDIA_LICENSE_OPTIONS.ALL
            ? [MEDIA_LICENSE_OPTIONS.EDITORIAL, MEDIA_LICENSE_OPTIONS.STOCK]
            : [selectedLicense as MEDIA_LICENSE_OPTIONS];
    return [...sourceFilterList, ...typeFilters, ...licenseFilters];
}

export function areMediaResultsFiltered(state: RootState, searchContext: SearchContextValueType) {
    /**
     * returns a boolean indicating if results are being filtered at all (ie. 1+ filters enabled)
     */
    // first check the type radio – if it's not all, it's filtering!
    const selectedType = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('selectedType')
        : state.media[searchContext].selectedType;
    if (selectedType !== MEDIA_TYPE_OPTIONS.ALL) {
        return true;
    }

    // check the licensing type radio – if it's not all, it's filtering!
    const selectedLicensingType = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('selectedLicense')
        : state.media[searchContext].selectedLicense;
    if (selectedLicensingType !== MEDIA_LICENSE_OPTIONS.ALL) {
        return true;
    }

    // now we want to confirm that none of the source filters are checked
    const sourceFiltersMap = userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('sourceFilter')
        : state.media[searchContext].sourceFilter;

    if (!sourceFiltersMap) {
        throw new Error('sourceFiltersMap is undefined');
    }

    const allEnabled = Object.values(sourceFiltersMap.toJS()).every((val) => val === true);
    return !allEnabled;
}

export function getActiveMediaFilterByUserLevel(
    state: RootState,
    searchContext: SearchContextValueType,
): FILTER_BY_LEVELS {
    return state.media[searchContext].filter_by_user_level;
}

export function getActiveMediaAjax(state: RootState, searchContext: SearchContextValueType) {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('ajax')
        : state.media[searchContext].ajax;
}

export function getActiveMediaMoreResults(state: RootState, searchContext: SearchContextValueType) {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('more_results')
        : state.media[searchContext].more_results;
}

export function getActiveMediaLastSearchedTerm(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].last_searched_term;
}

export function getLastUsedFilters(state: RootState, searchContext: SearchContextValueType) {
    return state.media[searchContext].last_used_filters;
}

export function getActiveMediaNoAccess(state: RootState, searchContext: SearchContextValueType) {
    return state.media[searchContext].no_access;
}

export function getActiveMediaNoResults(state: RootState, searchContext: SearchContextValueType) {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('no_results')
        : state.media[searchContext].no_results;
}

export function getActiveMediaSourceFilterValue(
    state: RootState,
    searchContext: SearchContextValueType,
    filter_key: any,
) {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('sourceFilter')?.get(filter_key) !==
              false
        : state.media[searchContext].sourceFilter.get(filter_key) !== false;
}

export function getActiveMediaTypeFilterChecked(
    state: RootState,
    searchContext: SearchContextValueType,
    filter_key: any,
) {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('selectedType') === filter_key
        : state.media[searchContext].selectedType === filter_key;
}

export function getActiveMediaLicenseFilterChecked(
    state: RootState,
    searchContext: SearchContextValueType,
    license_key: any,
) {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderDetails(state, searchContext)?.get('selectedLicense') === license_key
        : state.media[searchContext].selectedLicense === license_key;
}

export function getActiveMediaSort(
    state: RootState,
    searchContext: SearchContextValueType,
): SortEnum | null {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderSort(state, searchContext)
        : state.media[searchContext].sort;
}

export function getSearchResultById(
    state: RootState,
    searchContext: SearchContextValueType,
    media_id: any,
) {
    return userInUploadsFolder(state, searchContext)
        ? getActiveFolderMediaById(state, searchContext, media_id)
        : getMediaResult(state, searchContext, media_id);
}

export const getMediaIdsPrioritizedWithCurrentSessionUploads = createSelector(
    /*
    This selector returns all the media_ids in the section of the media library specified by searchContext.
    If we are in an uploads searchContext, some search results could have `current_session_upload = true`.
    We move those to the front of the list, so they are displayed first in the UI.
    */
    (state: RootState, searchContext: SearchContextValueType) =>
        selectAllMediaResults(state, searchContext),
    (mediaResults) => {
        const orm = new StoreORM<AssetContainerModel, string>(mediaResults);
        const currentSessionUploadsIds = orm.queryIds({
            key: ['current_session_upload'],
            value: true,
        });
        const previouslyUploadedIds = orm.queryIds({
            key: ['current_session_upload'],
            value: undefined,
        });

        if (currentSessionUploadsIds.size > 0) {
            // prioritize current session uploads first
            return currentSessionUploadsIds.concat(previouslyUploadedIds);
        }

        return previouslyUploadedIds;
    },
);

export function getMediaResult(
    state: RootState,
    searchContext: SearchContextValueType,
    media_id: any,
) {
    const orm = getMediaResultsORM(state, searchContext);
    return orm.getByIdOrUndefined(media_id);
}

export function getMediaSourceById(
    state: RootState,
    searchContext: SearchContextValueType,
    id: any,
) {
    const orm = getMediaResultsORM(state, searchContext);
    const asset = orm.getById(id);
    if (immutableAssetContainerModelIsAudio(asset)) {
        return asset.get(AssetContainerTypes.AUDIO).get('source');
    }
    if (immutableAssetContainerModelIsBackground(asset)) {
        const slideBackground = asset.get(asset.get('type'));

        if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(slideBackground)) {
            return slideBackground.get(SlideBackgroundModelTypeEnum.IMAGE).get('source');
        } else if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(slideBackground)) {
            return slideBackground.get(SlideBackgroundModelTypeEnum.VIDEO).get('source');
        }
    }
    if (immutableAssetContainerModelIsIcon(asset)) {
        return asset.get('icon').get('source');
    }
    throw new Error('unknown asset container type!');
}

export function getMediaResultAssetId(
    state: RootState,
    searchContext: SearchContextValueType,
    media_id: any,
) {
    const orm = getMediaResultsORM(state, searchContext);
    const asset = orm.getById(media_id);
    if (immutableAssetContainerModelIsAudio(asset)) {
        return asset.get(AssetContainerTypes.AUDIO).get('uuid');
    }
    if (immutableAssetContainerModelIsBackground(asset)) {
        const slideBackground = asset.get(asset.get('type'));

        if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(slideBackground)) {
            return slideBackground.get(SlideBackgroundModelTypeEnum.IMAGE).get('uuid');
        } else if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(slideBackground)) {
            return slideBackground.get(SlideBackgroundModelTypeEnum.VIDEO).get('uuid');
        }
    }
    if (immutableAssetContainerModelIsIcon(asset)) {
        return asset.get(AssetContainerTypes.ICON).get('uuid');
    }
    throw new Error('unknown asset container type!');
}

export function getMediaResultCount(state: RootState, searchContext: SearchContextValueType) {
    const orm = getMediaResultsORM(state, searchContext);
    return orm.length;
}

/**
 * Returns the current SEARCH_CONTEXT ('uploaded-audio', etc) for the currently selected EDITOR_TAB ('music', etc)
 * If the currently selected tab is NOT a search tab (Format, Style, etc) we'll return `undefined`.
 * NOTE: This should only be used when we're _actually_ looking for the currently selected tab's search context.
 *       In most components we want them to not re-render when the selected tab changes (ie. <AudioAsset> shouldn't
 *       update when we switch between the Icons & Format tabs). So you'll likely want to use
 *       `getActiveSearchContextForTab()` instead
 * @returns SEARCH_CONTEXT
 */
export function getActiveSearchContextForCurrentTab(
    state: RootState,
): SearchContextValueType | undefined {
    const editorTab = getCurrentEditorTab(state);
    return getActiveSearchContextForTab(state, editorTab);
}

/**
 * Returns the current SEARCH_CONTEXT ('uploaded-audio', etc) for the provided EDITOR_TAB ('music', etc)
 * @returns SEARCH_CONTEXT
 */
export function getActiveSearchContextForTab(
    state: RootState,
    editorTab: any,
): SearchContextValueType {
    return state.media.search_context.get(editorTab);
}

export function getRecommendedSearchTermsForSearchContext(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    const categoriesWithoutRecommendedSearchTerms = [
        SEARCH_CONTEXT.STOCK_AUDIO,
        SEARCH_CONTEXT.UPLOADED_AUDIO,
        SEARCH_CONTEXT.RECENTS_AUDIO,
        SEARCH_CONTEXT.FAVORITE_AUDIO,
    ];
    if (
        categoriesWithoutRecommendedSearchTerms.includes(
            searchContext as (typeof categoriesWithoutRecommendedSearchTerms)[number],
        )
    ) {
        return emptyImmutableList as Immutable.List<string>;
    }
    return state.undoable.present.media.recommended_search_terms;
}

export function getIsFetchingRecommendedSearchTerms(state: RootState) {
    return state.undoable.present.media.is_fetching_recommended_search_terms;
}

export function getRecommendedSearchTerms(state: RootState, searchContext: SearchContextValueType) {
    return getRecommendedSearchTermsForSearchContext(state, searchContext);
}

export function getAllFavoritesMediaORM(state: RootState) {
    return new StoreORM(state.media.all_favorite_media);
}

export function getIsFavouriteMedia(state: RootState, media_id: any) {
    return getAllFavoritesMediaORM(state).contains(media_id);
}

export function getPlayingTrackUrl(state: RootState) {
    return state.media.playingAudioDetails.get('url');
}

export function getPlayingTrackSeekPercent(state: RootState) {
    return state.media.playingAudioDetails.get('seekPercent');
}

export function getPlayingTrackScrubberSeekPercent(state: RootState) {
    return state.media.playingAudioDetails.get('scrubberSeekPercent');
}

export function getPlayingTrackDuration(state: RootState) {
    return state.media.playingAudioDetails.get('duration');
}

export function getPlayingTrackStatus(state: RootState) {
    return state.media.playingAudioDetails.get('isPlaying');
}

export function hasFolders(state: RootState) {
    return !!state.media?.all_uploads_folders;
}

export function selectRecordingFolder(state: RootState, name: string = RECORDING_FOLDER_NAME) {
    if (hasFolders(state)) {
        const orm = new StoreORM<MediaFolderWithContents>(state.media.all_uploads_folders);
        const folder = orm
            .query({
                key: ['name'],
                value: name,
            })
            ?.get(0);
        return folder;
    }
}

export function selectScriptMediaFolder(state: RootState) {
    if (hasFolders(state)) {
        const orm = new StoreORM<MediaFolderWithContents>(state.media.all_uploads_folders);
        return orm
            .query({
                key: ['name'],
                value: SCRIPT_COMPOSER_MEDIA_FOLDER_NAME,
            })
            ?.get(0);
    }
}

export function selectFolderByName(state: RootState, folderName: string) {
    if (hasFolders(state)) {
        const orm = new StoreORM<MediaFolderWithContents>(state.media.all_uploads_folders);
        return orm
            .query({
                key: ['name'],
                value: folderName,
            })
            ?.get(0);
    }
}

export const selectAllFolders = createSelector(
    (state: RootState) => state.media.all_uploads_folders,
    (allFolders): Immutable.List<ImmutableMap<MediaFolderWithContents>> => {
        if (!allFolders) {
            return Immutable.List();
        }
        const orm = new StoreORM<MediaFolderWithContents>(allFolders);
        return orm.map((folder) => folder);
    },
);

export function selectActiveMediaFolder(
    state: RootState,
    searchContext: SearchContextValueType | null,
): number | null {
    if (hasFolders(state) && searchContext) {
        return state.media.active_uploads_folder.get(searchContext, null);
    } else {
        return null;
    }
}

export function getActiveFolderDetails(state: RootState, searchContext: SearchContextValueType) {
    if (hasFolders(state)) {
        const activeFolderId = selectActiveMediaFolder(state, searchContext);
        if (activeFolderId) {
            const orm = new StoreORM<MediaFolderWithContents>(state.media.all_uploads_folders);
            return orm.getById(activeFolderId);
        }
    }
    return null;
}

export function getActiveFolderSort(state: RootState, searchContext: SearchContextValueType) {
    if (hasFolders(state)) {
        const activeFolderId = selectActiveMediaFolder(state, searchContext);
        if (activeFolderId) {
            const orm = new StoreORM<MediaFolderWithContents>(state.media.all_uploads_folders);
            const activeFolder = orm.getById(activeFolderId);

            return activeFolder.get('sort');
        }
    }
    return null;
}

export function getFolderDetailsById(state: RootState, folderId: number | null) {
    if (hasFolders(state) && folderId) {
        const orm = new StoreORM<MediaFolderWithContents>(state.media.all_uploads_folders);
        return orm.getById(folderId);
    }
    return null;
}

export function getFolderDetailsByNameAndSource(
    state: RootState,
    folderName: string,
    creationSource: SearchContextValueType,
) {
    if (hasFolders(state)) {
        const orm = new StoreORM<MediaFolderWithContents>(state.media.all_uploads_folders);

        const matchedFolders: Immutable.List<ImmutableMap<MediaFolderWithContents>> = orm
            .query({
                key: ['name'],
                value: folderName,
            })
            .filter((folder) => folder.get('creation_source') === creationSource);

        const firstMatch = matchedFolders.first();
        if (firstMatch) {
            return firstMatch;
        } else {
            return null;
        }
    }
    return null;
}

export function getActiveFolderMediaById(
    state: RootState,
    searchContext: SearchContextValueType,
    mediaId: any,
) {
    const activeFolder = getActiveFolderDetails(state, searchContext);
    if (activeFolder) {
        const folderMediaStoreOrm = new StoreORM<AssetContainerModel, string>(
            activeFolder.get('media_results'),
        );
        const media = folderMediaStoreOrm.getByIdOrUndefined(mediaId);
        return media;
    } else {
        return null;
    }
}

export function getMediaQueryResultByIndex(
    state: RootState,
    queryIndex: number,
    mediaIndex: number,
): ImmutableMap<AssetContainerModel> | null {
    const results = state.media.media_query_search_results;
    const result = results.get(queryIndex)?.get('results')?.get(mediaIndex);
    return result || null;
}

export function getSimilarSearchResultByIndex(
    state: RootState,
    mediaIndex: number,
    isAIContext = false,
): ImmutableMap<AssetContainerModel> | undefined {
    if (isAIContext) {
        const asset = state.media.ai_similar_generations[mediaIndex]?.result.asset;
        if (asset) {
            return ImmutableFromJS(asset);
        }
        return undefined;
    }
    return state.media.similar_search_results.get(mediaIndex);
}

export function getIsSimilarSearchContext(state: RootState) {
    return state.media.is_similar_search_context;
}

export function getSimilarSearchOriginalAsset(state: RootState) {
    return state.media.similar_search_original_asset;
}

export function getSimilarSearchOriginalAssetUuid(state: RootState) {
    const assetContainer = getSimilarSearchOriginalAsset(state);
    const background = assetContainer?.get(AssetContainerTypes.BACKGROUND);
    if (background) {
        if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
            return background.get(SlideBackgroundModelTypeEnum.VIDEO).get('uuid') || null;
        } else if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
            return background.get(SlideBackgroundModelTypeEnum.IMAGE).get('uuid') || null;
        }
    }
    return null;
}

export function getCurrentMediaGenerations(state: RootState) {
    return state.media.current_media_generations;
}

export function getPreviousMediaGenerations(state: RootState) {
    return state.media.previous_media_generations;
}

export function getMediaGenerationByIndex(state: RootState, index: number) {
    // Search both current and previous generations to find the media.
    // NOTE: Ensure that the index is the cumulative index between both lists
    return getCurrentMediaGenerations(state).concat(getPreviousMediaGenerations(state))[index];
}

export function getGenerationFromMediaUuid(
    state: RootState,
    mediaUuid: string,
    mediaType: SlideBackgroundModelTypeEnum,
) {
    // First check the current and previous generations list used in the AI Media tab
    const currentAndPreviousGenerations = [
        ...getCurrentMediaGenerations(state),
        ...getPreviousMediaGenerations(state),
    ];

    const foundinAIMediaList = findGenerationInList(
        currentAndPreviousGenerations,
        mediaUuid,
        mediaType,
    );
    if (foundinAIMediaList) return foundinAIMediaList;

    // We haven't found the generation in the AI Media tab, so check the similar generations list
    const foundInSimilar = findGenerationInList(
        getAISimilarGenerations(state),
        mediaUuid,
        mediaType,
    );
    if (foundInSimilar) return foundInSimilar;

    return null;
}

export function getIsSavingAIMediaForGeneration(state: RootState, generationUuid: string): boolean {
    const generations = [
        ...getCurrentMediaGenerations(state),
        ...getPreviousMediaGenerations(state),
    ];
    const generation = generations.find((g) => g?.result.uuid === generationUuid);
    return generation?.result.isSaving || false;
}

export function getIsAISimilarGenerationLoading(state: RootState) {
    return state.media.ai_similar_generations_loading;
}

export function getAISimilarGenerations(state: RootState): ModifiedAssetGenerationResponse[] {
    return state.media.ai_similar_generations;
}

export function getSelectedReferenceImage(state: RootState): UploadedFile | null {
    return state.media.selected_reference_image;
}

export function getMediaErrors(state: RootState) {
    return state.media?.media_errors;
}

export function getActiveAudioDurationFilter(
    state: RootState,
    searchContext: SearchContextValueType,
) {
    return state.media[searchContext].audioDurationFilter;
}

export const getSearchContextFolders = createSelector(
    selectAllFolders,
    (_: RootState, searchContext: SearchContextValueType) => searchContext,
    (allFolders, searchContext) => {
        return allFolders.filter((folder) => folder.get('creation_source') === searchContext);
    },
);

export const getNonEmptyFolders = createSelector(getSearchContextFolders, (folders) => {
    return folders.filter((folder) => folder.get('media_count') > 0);
});

export const getTalkingHeadMediaResults = createSelector(getMediaResultsORM, (mediaResultORM) => {
    return mediaResultORM.filter((media) => {
        if (!immutableAssetContainerModelIsBackground(media)) {
            return false;
        }
        const background = media.get(AssetContainerTypes.BACKGROUND);
        if (!immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
            return false;
        }
        const video = background.get(SlideBackgroundModelTypeEnum.VIDEO);
        if (!video) {
            return false;
        }
        const hasVoiceover = !!video.get('has_voiceover');
        if (!hasVoiceover) {
            return false;
        }
        return true;
    });
});

export function getAIMediaModels(state: RootState) {
    return state.media.ai_media_models;
}

export function getVideoGenerationTasks(state: RootState) {
    return state.media.video_generation_tasks;
}

export function getVideoGenerationAssetData(
    state: RootState,
    taskId: string,
): ImmutableMap<AssetContainerModel> | null {
    const task = getVideoGenerationTasks(state)[taskId];
    if (!task?.result) {
        return null;
    }
    return convertImmutableSlideBackgroundToImmutableAssetContainer(ImmutableFromJS(task.result));
}

export function getChartGenerationAsset(state: RootState): AssetContainerModel | null {
    return state.media.chart_generation_asset;
}

export function getChartGenerationAssetData(
    state: RootState,
): ImmutableMap<AssetContainerModel> | null {
    const asset = getChartGenerationAsset(state);
    if (!asset) {
        return null;
    }
    return ImmutableFromJS(asset);
}

export function getIsInitializingVideoGeneration(state: RootState) {
    return state.media.is_initializing_video_generation;
}

export function getIsInitializingImageGeneration(state: RootState) {
    return state.media.is_initializing_image_generation;
}

export function getActiveAIMediaSubTab(state: RootState) {
    return state.media.active_ai_media_sub_tab;
}

export function getUserSelectedGenMediaAspectRatio(state: RootState): string | null {
    return state.media.user_selected_gen_media_aspect_ratio;
}
